import FormattedMessage from 'I18n/components/FormattedMessage';
import QuantityNames from 'merchandise-lib/constants/QuantityName';
import FormattedNumber from 'I18n/components/FormattedNumber';
import FormattedReactMessage from 'I18n/components/FormattedReactMessage';
import { Text } from 'foundations-components/components/Text';
import { CardSection, CardWrapper } from 'foundations-components/transitional/components/Card';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import { StepperInput } from 'foundations-components/transitional/components/StepperInput';
import { createProductConfig } from 'pricing-pages-lib/utils/productConfigUtils';
import { PriceDisplay } from 'pricing-pages-ui-components/price/PriceDisplay';
import PurchaseMotionButtonWrapper from 'pricing-pages-ui/components/common/purchaseMotions/PurchaseMotionButtonWrapper';
import { useState } from 'react';
import { useGlobalLoading } from '../../hooks/loadingHooks';
import { useCalculatedPrice } from '../../hooks/useBulkPurchaseOptions';
import { useDisplayedCurrencyCode } from '../../hooks/useCurrencies';
import { useTrackingFlow } from '../../hooks/useTrackingFlow';
import { useAppSelector } from '../../reducers';
import { checkoutProductConfigurationsSelector } from '../../selectors/ProductConfigsSelectors';
import { highestPackagedQuantityForByUnitNameFromProductInventorySelector, productsFromProductInventorySelector } from '../../selectors/ProductInventorySelectors';
import { packagedQuantityBySkuIdSelector, productVersionSelector } from '../../selectors/ProductsSelectors';
import { purchaseMotionsSelector } from '../../selectors/PurchaseMotionSelectors';
import { useQueryParamTerm } from '../../utils/urls/appQueryParamUtilsLegacy';
import { CustomError } from '../../v2/components/error-boundary/CustomErrors';
import { withErrorBoundary } from '../../v2/components/error-boundary/withErrorBoundary';
import { APP } from '../../v2/constants/tracking/TrackingSources';
import { useHubSpotCreditsInventoryInfo } from '../../v2/hooks/inventory/useHubSpotCreditsInventoryInfo';
import { trackAdditionalUnitsPicker, trackBuyButtonClick, trackTalkToSalesClick } from '../../v2/utils/tracking/trackingEventUtils';
import SkeletonLoadWrapper from '../common/loading/SkeletonLoadWrapper';
import { calculateMaxTouchlessPurchasableQuantity } from '../../v2/utils/quantityPackUtils';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const BuyAdditionalCreditsCard = ({
  screen,
  trackingSource
}) => {
  const isAppReady = useGlobalLoading();
  const includedWithPlan = useAppSelector(state => {
    return highestPackagedQuantityForByUnitNameFromProductInventorySelector(state, {
      unitName: QuantityNames.HUBSPOT_CREDITS
    });
  });
  const {
    ownedHubSpotCreditsTotal: ownedQuantity,
    ownedHubSpotCreditsSkuId,
    ownedHubspotCreditsApiName: apiName,
    loading: hubSpotCreditsLoading
  } = useHubSpotCreditsInventoryInfo();
  const ownedHubSpotCreditsVersion = useAppSelector(state => {
    if (!ownedHubSpotCreditsSkuId || hubSpotCreditsLoading) return null;
    return productVersionSelector(state, {
      skuId: ownedHubSpotCreditsSkuId
    });
  });
  const [selectedQuantity, setSelectedQuantity] = useState(0);
  const packagedQuantities = useAppSelector(state => {
    if (!ownedHubSpotCreditsSkuId) {
      return null;
    }
    return packagedQuantityBySkuIdSelector(state, {
      skuId: ownedHubSpotCreditsSkuId,
      unitName: QuantityNames.HUBSPOT_CREDITS
    });
  });
  const productConfigurations = useAppSelector(state => checkoutProductConfigurationsSelector(state, {
    productConfigurations: ownedHubSpotCreditsSkuId ? [createProductConfig({
      skuId: ownedHubSpotCreditsSkuId,
      quantity: ownedQuantity + selectedQuantity || 1
    })] : []
  }));
  const maxTouchlessPurchasableQuantity = ownedHubSpotCreditsVersion ? ownedHubSpotCreditsVersion.maxTouchlessPurchasableQuantity : null;
  const maxQuantityForStepper = calculateMaxTouchlessPurchasableQuantity(maxTouchlessPurchasableQuantity);
  const minQuantityForStepper = 0;
  const ownedProducts = useAppSelector(state => productsFromProductInventorySelector(state).map(product => product.apiName));
  const purchaseMotionLocation = 'CALCULATOR';
  const purchaseMotions = useAppSelector(state => {
    if (!isAppReady || !ownedHubSpotCreditsSkuId) return [];
    return purchaseMotionsSelector(state, {
      skuId: ownedHubSpotCreditsSkuId,
      location: purchaseMotionLocation
    });
  });
  const purchaseMotion = purchaseMotions[0];
  const isTouchlessPurchase = purchaseMotion === 'BUY_TOUCHLESS';

  // disable the button if not touchless or the user hasn't increased the stepper to more than already owned
  const disabledButton = isTouchlessPurchase &&
  // only disable if touchless purchase is allowed (always let talk to sales be clickable)
  selectedQuantity <= minQuantityForStepper;
  const trackingData = {
    app: APP,
    screen,
    uniqueId: `${trackingSource}-${apiName}`
  };
  const currencyCode = useDisplayedCurrencyCode();
  const {
    queryParamTerm
  } = useQueryParamTerm();
  const {
    monthlyCalculatedPrice,
    loading: pricingInfoLoading,
    error
  } = useCalculatedPrice({
    apiName,
    queryParamTerm
  });
  const flow = useTrackingFlow({
    productConfigurations,
    purchaseMotion
  });
  if (!ownedHubSpotCreditsSkuId || !packagedQuantities) return null;
  if (error) {
    throw new CustomError('Failed calculating price', {
      error,
      apiName,
      queryParamTerm
    });
  }
  const handleButtonClick = () => {
    if (isTouchlessPurchase) {
      trackBuyButtonClick({
        purchaseMotion: 'BUY_TOUCHLESS',
        productNames: apiName ? [apiName] : [],
        ownedProducts,
        flow,
        trackingSource
      });
    } else {
      trackTalkToSalesClick({
        trackingSource,
        productNames: apiName ? [apiName] : [],
        ownedProducts,
        flow
      });
    }
  };
  const handleStepperChange = evt => {
    const input = Math.round(parseInt(evt.target.value, 10) / packagedQuantities);
    if (isNaN(input)) return;
    const newQuantity = Math.max(minQuantityForStepper, Math.min(maxQuantityForStepper, input));
    trackAdditionalUnitsPicker({
      action: `stepper ${newQuantity > selectedQuantity ? 'increase' : 'decrease'}`,
      trackingSource,
      activeCurrencyCode: currencyCode,
      skuId: ownedHubSpotCreditsSkuId,
      ownedUnitQuantity: ownedQuantity || 0,
      displayedUnitQuantity: selectedQuantity * packagedQuantities,
      productName: apiName,
      ownedProducts
    });
    setSelectedQuantity(newQuantity);
  };
  const loading = !isAppReady || pricingInfoLoading || hubSpotCreditsLoading;
  if (loading || !apiName) return /*#__PURE__*/_jsx(LoadingSpinner, {
    grow: true
  });
  if (!monthlyCalculatedPrice) return null;
  return /*#__PURE__*/_jsxs(CardWrapper, {
    inlineSize: "100%",
    paddingBlock: "800",
    paddingInline: "1000",
    marginBlockEnd: "0",
    display: "flex",
    flexDirection: "column",
    gap: "600",
    "data-test-id": `${apiName}-merch-card`,
    "data-selenium-id": `${apiName}-merch-card`,
    children: [/*#__PURE__*/_jsxs(CardSection, {
      padding: "0",
      gap: "300",
      children: [/*#__PURE__*/_jsx(Text, {
        whiteSpace: "nowrap",
        as: "h2",
        typography: "heading-200",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "pricingPage.pages.hubspot-credits.info.card.buyAdditionalCredits"
        })
      }), /*#__PURE__*/_jsx(Text, {
        as: "p",
        typography: "body-100",
        children: /*#__PURE__*/_jsx(FormattedReactMessage, {
          message: "pricingPage.pages.hubspot-credits.info.card.includedAndAdditionalCredits",
          options: {
            includedQty: /*#__PURE__*/_jsx(FormattedNumber, {
              value: includedWithPlan
            }),
            additionalQty: /*#__PURE__*/_jsx(FormattedNumber, {
              value: ownedQuantity * packagedQuantities
            })
          }
        })
      })]
    }), /*#__PURE__*/_jsxs(CardSection, {
      padding: "0",
      gap: "300",
      display: "flex",
      flexDirection: "column",
      children: [/*#__PURE__*/_jsx(StepperInput, {
        "data-selenium-id": `${trackingSource}-${apiName}-stepper-input`,
        maxInlineSize: "140px",
        stepSize: packagedQuantities,
        value: selectedQuantity * packagedQuantities,
        min: minQuantityForStepper * packagedQuantities,
        max: maxQuantityForStepper * packagedQuantities,
        onChange: handleStepperChange
      }), /*#__PURE__*/_jsx(SkeletonLoadWrapper, {
        isLoading: loading,
        heights: [20],
        widths: [75],
        children: /*#__PURE__*/_jsx(Text, {
          typography: "heading-50",
          as: "span",
          children: /*#__PURE__*/_jsx(FormattedReactMessage, {
            message: "priceDisplay.highlightedPrice.pricePerCredits",
            options: {
              price: /*#__PURE__*/_jsx(PriceDisplay, {
                price: monthlyCalculatedPrice,
                currencyCode: currencyCode
              }),
              packagedQuantities
            }
          })
        })
      })]
    }), /*#__PURE__*/_jsx(CardSection, {
      padding: "0",
      children: /*#__PURE__*/_jsx(PurchaseMotionButtonWrapper, {
        apiName: apiName,
        queryParamTerm: queryParamTerm,
        use: "primary",
        purchaseMotion: purchaseMotion,
        trackingSource: trackingSource,
        trackingData: trackingData,
        productConfigurations: productConfigurations,
        disabled: disabledButton,
        onClick: handleButtonClick,
        skipInterstitialPage: true,
        purchaseMotionLocation: purchaseMotionLocation,
        block: true
      })
    })]
  });
};
export default withErrorBoundary(BuyAdditionalCreditsCard, {
  fallback: null,
  errorBoundaryName: 'BuyAdditionalCreditsCard'
});